import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Typography
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import User1 from 'assets/images/users/user-round.svg';

// assets
import { IconLogout, IconSettings } from '@tabler/icons';
import useAuth from 'Auth/useAuth';
import { t } from 'i18next';
import useAlert from 'contexts/alertProvider/useAlert';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const alert = useAlert();
    const auth = useAuth();
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);

    const selectedIndex = -1;
    const [open, setOpen] = useState(false);
    const user = JSON.parse(localStorage.getItem('user'));
    var userName = 'username';
    var email;
    var language;
    if (user) {
        userName = user.name;
        email = user.email;
        language = user.language;
    }

    const anchorRef = useRef(null);
    const handleLogout = async () => {
        auth.logout().then((response) => {
            if (!(response.status == 200 || response.status == 204)) {
                if (response.status == -1) {
                    alert.error(t('p.server_not_found'));
                } else {
                    alert.error(response.error_description);
                }
            }
        });
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Chip
                title="avatar-button"
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar
                        src={User1}
                        alt="Avatar"
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                    />
                }
                label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />
            <Popper
                title="avatar-button"
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Box sx={{ p: 1.5 }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%'
                                                }}
                                            >
                                                <ListItem>
                                                    <Typography variant="h4">{t('w.welcome')}&nbsp;</Typography>
                                                    <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                        {userName}
                                                    </Typography>
                                                </ListItem>
                                            </List>
                                        </Stack>
                                        <ListItem>
                                            <Typography component="span" variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                Email:&nbsp;
                                            </Typography>
                                            <Typography component="span" variant="subtitle2">
                                                {email}
                                            </Typography>
                                        </ListItem>
                                        <ListItem>
                                            <Typography component="span" variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {t('w.language')}:&nbsp;
                                            </Typography>
                                            <Typography component="span" variant="subtitle2">
                                                {language}
                                            </Typography>
                                        </ListItem>
                                    </Box>
                                    <Divider />
                                    <Box sx={{ p: 1 }}>
                                        <List
                                            component="nav"
                                            sx={{
                                                width: '100%',
                                                maxWidth: 350,
                                                minWidth: 300,
                                                backgroundColor: theme.palette.background.paper,
                                                borderRadius: '10px',
                                                [theme.breakpoints.down('md')]: {
                                                    minWidth: '100%'
                                                },
                                                '& .MuiListItemButton-root': {
                                                    mt: 0
                                                }
                                            }}
                                        >
                                            <ListItemButton
                                                sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                selected={selectedIndex === 4}
                                                onClick={handleLogout}
                                            >
                                                <ListItemIcon>
                                                    <IconLogout stroke={1.5} size="1.3rem" />
                                                </ListItemIcon>
                                                <ListItemText primary={<Typography variant="body2">{t('w.logout')}</Typography>} />
                                            </ListItemButton>
                                        </List>
                                    </Box>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
